import { defineStore } from 'pinia';
import { computed, ComputedRef, Ref, ref } from 'vue';
import { Alert, Banner, InboxNotification } from '@4csoftware/api-types';
import { useAPI } from '@/composables/useAPI';

export const useNotificationStore = defineStore(
  'notification',
  () => {
    const banners: Ref<Banner[]> = ref([]);
    const alerts: Ref<Alert[]> = ref([]);
    const priorityAlerts: Ref<Alert[]> = ref([]);

    const notifications: Ref<InboxNotification[]> = ref([]);
    const numUnread: ComputedRef<number> = computed(() => {
      return notifications.value.filter((notification) => !notification.read).length;
    });

    function addBanner(banner: Banner) {
      banners.value.push(banner);
    }

    function addAlert(alert: Alert) {
      if (typeof alert.id !== 'string') {
        alert.id = crypto.randomUUID();
      }
      alerts.value.push(alert);
    }

    function addPriorityAlert(alert: Alert) {
      // Unshift so the newest alert sits at index 0 — that's the slot the
      // toast renders, and it's also what the user expects after asking for
      // "newest at the top". Older fills slide behind as ghost cards.
      priorityAlerts.value.unshift(alert);
    }

    function addInboxNotification(notification: InboxNotification, opts: { alertType: 'priority' | 'default' | 'none' }) {
      notifications.value.unshift(notification);
      switch (opts.alertType) {
        case 'priority':
          addPriorityAlert(notification.content);
          break;
        case 'default':
          addAlert(notification.content);
          break;
      }
    }

    async function acknowledgeAndRemoveBanner(bannerId: string) {
      const index = banners.value.findIndex((banner) => banner.bannerID === bannerId);
      if (index !== -1) {
        banners.value.splice(index, 1);
        // Acknowledge the banner with the backend
        try {
          const { request } = useAPI('user/acknowledgeBanner');
          await request({
            bannerID: bannerId,
          });
        } catch (error) {
          console.error('Error acknowledging banner:', error);
        }
      }
    }

    function removeAlert_Internal(alertId: string) {
      const index = alerts.value.findIndex((alert) => alert.id === alertId);
      if (index !== -1) {
        alerts.value.splice(index, 1);
      }
    }

    function removeAlert(alertId: string) {
      removeAlert_Internal(alertId);
      // Inbox notifications also show up as alerts, so if we remove an alert, we need to mark the inbox notification as read
      markInboxNotificationAsRead_Internal(alertId, { updateBackend: true });
    }

    function removePriorityAlert_Internal(alertId: string) {
      const index = priorityAlerts.value.findIndex((alert) => alert.id === alertId);
      if (index !== -1) {
        priorityAlerts.value.splice(index, 1);
      }
    }

    function removePriorityAlert(alertId: string) {
      removePriorityAlert_Internal(alertId);
      // Inbox notifications also show up as alerts, so if we remove an alert, we need to mark the inbox notification as read
      // If the alert is not an inbox notification, this has no effect
      markInboxNotificationAsRead_Internal(alertId, { updateBackend: true });
    }

    function setLocalNotificationAsRead(notificationID: string) {
      const index = notifications.value.findIndex((notification) => notification.id === notificationID);
      if (index !== -1) {
        notifications.value[index].read = true;
      }
    }

    async function markInboxNotificationAsRead_Internal(notificationID: string, opts: { updateBackend: boolean }) {
      const hasInboxNotification = notifications.value.some((notification) => notification.id === notificationID);
      if (!hasInboxNotification) return;
      setLocalNotificationAsRead(notificationID);
      if (opts.updateBackend) {
        const { request: ackNotifications } = useAPI('/user/ackNotifications');
        await ackNotifications({
          notificationIDs: [notificationID],
        });
      }
    }

    async function markInboxNotificationAsRead(notificationID: string, opts: { updateBackend: boolean }) {
      markInboxNotificationAsRead_Internal(notificationID, opts);
      // The notification could have been shown as either an alert or a priority alert, so we need to remove it from both
      removeAlert_Internal(notificationID);
      removePriorityAlert_Internal(notificationID);
    }

    // Batch variant. Filters the caller-supplied list down to IDs that actually
    // back an inbox notification, flips them read locally, then sends one
    // /user/ackNotifications request instead of N. Mirrors the singular
    // function's local cleanup: removes each from both alert lanes.
    async function markInboxNotificationsAsRead(notificationIDs: string[], opts: { updateBackend: boolean }) {
      const inboxBacked = notificationIDs.filter((id) =>
        notifications.value.some((notification) => notification.id === id),
      );
      inboxBacked.forEach((id) => setLocalNotificationAsRead(id));
      notificationIDs.forEach((id) => {
        removeAlert_Internal(id);
        removePriorityAlert_Internal(id);
      });
      if (opts.updateBackend && inboxBacked.length > 0) {
        try {
          const { request: ackNotifications } = useAPI('/user/ackNotifications');
          await ackNotifications({ notificationIDs: inboxBacked });
        } catch (error) {
          console.error('Error acknowledging notifications:', error);
        }
      }
    }

    async function refreshInboxNotifications() {
      // fetch and set
      const { get: getNotifications } = useAPI('/user/getNotifications');
      const { notifications: fetchedNotifications } = await getNotifications();
      // Sort newest-first so the inbox list shows the most recent notification
      // at the top, regardless of how the backend orders the response.
      // Adding new ones via unshift keeps that invariant going forward.
      notifications.value = [...fetchedNotifications].sort((a, b) => {
        const at = new Date(a.createdAt).getTime();
        const bt = new Date(b.createdAt).getTime();
        return bt - at;
      });
    }

    async function refreshBanners() {
      // fetch and set
      const { get: getBanners } = useAPI('/user/getBannersForUser');
      const { banners: fetchedBanners } = await getBanners();
      banners.value = fetchedBanners;
    }

    async function markAllInboxNotificationsAsRead() {
      // Get all unread notification IDs
      const unreadNotifications = notifications.value.filter((notification) => !notification.read);
      if (unreadNotifications.length === 0) return;

      // Mark all as read locally first for immediate UI feedback
      unreadNotifications.forEach((notification) => {
        setLocalNotificationAsRead(notification.id);
      });

      // Then send to backend
      try {
        const { request: ackNotifications } = useAPI('/user/ackNotifications');
        await ackNotifications({
          notificationIDs: unreadNotifications.map((n) => n.id),
        });
      } catch (error) {
        console.error('Error acknowledging notifications:', error);
      }
    }

    function clearAlerts() {
      alerts.value = [];
    }

    function clearPriorityAlerts() {
      priorityAlerts.value = [];
    }

    function reset() {
      banners.value = [];
      notifications.value = [];
      alerts.value = [];
      priorityAlerts.value = [];
    }

    return {
      banners,
      notifications,
      alerts,
      priorityAlerts,
      numUnread,
      // Add
      addBanner,
      addAlert,
      addPriorityAlert,
      addInboxNotification,
      // Remove
      acknowledgeAndRemoveBanner,
      removeAlert,
      removePriorityAlert,
      markInboxNotificationAsRead,
      markInboxNotificationsAsRead,
      markAllInboxNotificationsAsRead,
      // Refresh/reset
      refreshInboxNotifications,
      refreshBanners,
      clearAlerts,
      clearPriorityAlerts,
      reset,
    };
  }
);
